// L'app posée sur l'écran d'accueil (D-102) : l'icône et le nom du club, ouverture en plein écran.
// Sans manifeste ni icône, l'iPhone pose un « B » gris, ouvre Safari et refuse les notifications.
// L'icône (PNG 512 × 512, fond opaque : l'iPhone noircit la transparence) est dessinée ici, dans l'app,
// quand le bureau enregistre ses couleurs et son logo, ou à la première ouverture d'un admin ; la base
// la garde (clubs.app_icon) et la fonction serveur club-app la sert, avec le manifeste du club.
import { monogram, palette, isHexColor } from '../domain/brand';
import { updateClubBrand, type ClubBrand } from '../lib/api';
import { supabaseUrl } from '../lib/supabase';
import { clubHostSlug } from '../domain/slug';

const SIZE = 512;
const DEFAULT = '#E8590C';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type IconSource = Pick<ClubBrand, 'name' | 'short_name' | 'logo_url' | 'primary_color'>;

/** Empreinte de ce qui fait l'icône : on ne la redessine que si le logo, la couleur ou le nom changent. */
export function appIconKey(b: IconSource): string {
  const s = `v1|${b.logo_url ?? ''}|${b.primary_color ?? ''}|${b.short_name ?? ''}|${b.name}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

/** L'icône du club : son logo sur fond blanc, sinon ses initiales sur sa couleur. */
export async function drawAppIcon(b: IconSource): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const g = canvas.getContext('2d')!;
  const color = isHexColor(b.primary_color) ? b.primary_color! : DEFAULT;
  if (b.logo_url) {
    const img = new Image();
    img.src = b.logo_url;
    await img.decode();
    g.fillStyle = '#FFFFFF';
    g.fillRect(0, 0, SIZE, SIZE);
    // Marge de 12 % : l'iPhone et Android arrondissent les coins.
    const box = SIZE * 0.76, k = Math.min(box / img.naturalWidth, box / img.naturalHeight);
    const w = img.naturalWidth * k, h = img.naturalHeight * k;
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
  } else {
    g.fillStyle = color;
    g.fillRect(0, 0, SIZE, SIZE);
    const text = monogram(b.name, b.short_name);
    g.fillStyle = palette(color).light.ink;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const px = text.length <= 2 ? 230 : text.length === 3 ? 180 : 140;
    g.font = `400 ${px}px Anton, Impact, "Arial Narrow", sans-serif`;
    g.fillText(text, SIZE / 2, SIZE / 2 + px * 0.04);
  }
  return canvas.toDataURL('image/png');
}

/** L'icône à jour pour un club, à enregistrer avec ses couleurs (null : rien à changer). */
export async function appIconPatch(b: IconSource & { app_icon_for?: string | null }): Promise<{ app_icon: string; app_icon_for: string } | null> {
  const key = appIconKey(b);
  if (b.app_icon_for === key) return null;
  return { app_icon: await drawAppIcon(b), app_icon_for: key };
}

/** Première ouverture d'un admin après un changement (ou d'un club créé avant D-102) : l'icône se fait toute seule. */
export async function ensureAppIcon(b: ClubBrand | null, isAdmin: boolean): Promise<void> {
  if (!b || !isAdmin || !UUID.test(b.id)) return;
  try {
    const patch = await appIconPatch(b);
    if (patch) { await updateClubBrand(b.id, patch); b.app_icon_for = patch.app_icon_for; applyAppHead(b); }
  } catch { /* hors ligne, image illisible : on réessaiera à la prochaine ouverture */ }
}

const setLink = (rel: string, href: string) => {
  let l = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!l) { l = document.createElement('link'); l.rel = rel; document.head.append(l); }
  if (l.getAttribute('href') !== href) l.setAttribute('href', href);
};
const setMeta = (name: string, content: string) => {
  let m = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!m) { m = document.createElement('meta'); m.name = name; document.head.append(m); }
  if (m.content !== content) m.content = content;
};

/** Le club d'une marque : son identifiant, ou l'adresse de sa page publique (« host:bcl » sur son sous-domaine,
 *  « public:bcl » sur /c/bcl) — la page publique ne connaît pas l'identifiant du club. */
function clubKey(b: ClubBrand | null): string | null {
  if (!supabaseUrl) return null;
  // Sur le sous-domaine d'un club (D-104) : son adresse, connue dès le chargement, avant toute connexion.
  const host = clubHostSlug(location.hostname, import.meta.env.VITE_CLUB_HOST_BASE as string | undefined);
  if (host) return `s=${host}`;
  if (!b) return null;
  if (UUID.test(b.id)) return `c=${b.id}`;
  const m = /^(?:host|public):([a-z0-9][a-z0-9-]{1,38}[a-z0-9])$/.exec(b.id);
  return m ? `s=${m[1]}` : null;
}

/** Manifeste, icône et nom de l'app sur l'écran d'accueil : ceux du club affiché (app ou page publique),
 *  sinon ceux d'Arceau. Posée depuis la page publique d'un club, l'app s'ouvre sur l'espace membres. */
let firstKey: string | null | undefined;   // le manifeste posé au chargement de la page
let lastKey: string | null = null;

/** Safari garde le manifeste du chargement : si la page a démarré sans le club (première connexion
 *  sur app.arceau.app, changement de club), il faut la recharger une fois pour poser le bon. */
export const appHeadChanged = (): boolean => firstKey !== undefined && firstKey !== lastKey;

/** Recharge une seule fois par session (jamais en boucle, même sans stockage). true : rechargement lancé. */
export function reloadForAppHead(): boolean {
  try {
    if (sessionStorage.getItem('arceau.apphead.reloaded')) return false;
    sessionStorage.setItem('arceau.apphead.reloaded', '1');
  } catch { return false; }
  location.reload();
  return true;
}

export function applyAppHead(b: ClubBrand | null): void {
  const key = clubKey(b);
  if (firstKey === undefined) firstKey = key;
  lastKey = key;
  setMeta('apple-mobile-web-app-capable', 'yes');
  setMeta('mobile-web-app-capable', 'yes');
  setMeta('apple-mobile-web-app-status-bar-style', 'default');
  if (!key) {
    setLink('manifest', '/manifest.json');
    setLink('apple-touch-icon', '/icons/apple-touch-icon.png');
    setMeta('apple-mobile-web-app-title', 'Arceau');
    return;
  }
  const fn = `${supabaseUrl}/functions/v1/club-app`;
  const v = b?.app_icon_for ?? '0';
  const onClubHost = key.startsWith('s=') && !location.pathname.startsWith('/c/');
  const path = location.pathname.startsWith('/app') || onClubHost ? '/app' : '/';
  const q = (f: string) => `${fn}?f=${f}&${key}&o=${encodeURIComponent(location.origin)}&p=${encodeURIComponent(path)}&v=${v}`;
  setLink('manifest', q('manifest'));
  setLink('apple-touch-icon', q('icon'));
  if (b && !b.id.startsWith('offer:') && !b.id.startsWith('derog:')) setMeta('apple-mobile-web-app-title', (b.short_name?.trim() || b.name).slice(0, 15));
}
